/**
 * Maps a blog category (or grass display name) to a short, lowercase topic
 * phrase for email-capture copy. Falls back to the raw category so grass
 * names ("Bermudagrass") pass through, or null when nothing is provided.
 *
 * Shared by the inline post-read CTA and the exit-intent newsletter modal so
 * both surfaces name the reader's topic identically (specificity rule —
 * obsidian/research/growth-design/design-blunders.md).
 *
 * ANALYTICS WARNING — the `topic` event property holds TWO different value
 * spaces, and a breakdown that ignores this reads as corrupt data:
 *
 *   - Modal surfaces (`*_modal`, via newsletter-signup-modal.tsx) emit the
 *     DISPLAY PHRASE this module returns: "weed control", "your grass type",
 *     "seasonal lawn care", "Bermudagrass". These are prose fragments spliced
 *     into copy, and they are correct.
 *   - Reminder-widget surfaces (`blog_reminder_widget_*`) emit the slug-shaped
 *     `ReminderTopic` KEY: "weed-control", "grass-identity", "seasonal".
 *
 * So `topic = "your grass type"` is NOT a bug and NOT a leaked copy fragment
 * (misdiagnosed as one during the 2026-08-08 capture audit). Always split a
 * topic breakdown by `source` first. Do not "normalize" these into one value
 * space: renaming either breaks every historical time series that joins on it.
 */
export function topicFromCategory(category?: string): string | null {
  const matched = strictTopicFromCategory(category)
  if (matched) return matched
  // Grass display names ("Bermudagrass", "Tall Fescue", etc.) won't match any
  // keyword. Return the raw category so copy names the specific grass the
  // reader just finished reading about.
  return category?.trim() || null
}

/**
 * Keyword-match-only variant: returns null when no care-topic keyword matches
 * instead of passing the raw category through. Use for blog categories, where
 * unmapped values are CMS taxonomy labels ("Problem Solving") that read badly
 * in copy — e.g. "Get your free Problem Solving care calendar". Guides keep
 * the passthrough since their input is a grass display name.
 */
export function strictTopicFromCategory(category?: string): string | null {
  if (!category) return null
  const c = category.toLowerCase()
  if (c.includes("weed") || c.includes("herbicide")) return "weed control"
  if (c.includes("pest") || c.includes("insect") || c.includes("grub")) return "pest control"
  if (c.includes("fertiliz") || c.includes("soil") || c.includes("nutrient")) return "fertilizing"
  if (c.includes("water") || c.includes("irrigation") || c.includes("drought")) return "watering"
  if (c.includes("mow") || c.includes("maintenance") || c.includes("cutting")) return "mowing"
  if (c.includes("seed") || c.includes("overseed") || c.includes("germinat")) return "seeding"
  if (c.includes("disease") || c.includes("fungus") || c.includes("lawn problem")) return "lawn disease"
  if (c.includes("seasonal") || c.includes("winter") || c.includes("spring") || c.includes("fall"))
    return "seasonal lawn care"
  if (c.includes("grass type") || c.includes("identification") || c.includes("species"))
    return "your grass type"
  return null
}
